/**
 * Utilities for checking active entitlements via Despia's
 * getpurchasehistory:// command and syncing to Supabase.
 *
 * Since Despia doesn't support a separate revenuecat://login command,
 * we check the local Apple receipt and sync the entitlement data
 * directly to the edge function with source: "device_receipt".
 */

import { useCallback, useRef, useState } from 'react';
import { isDespiaIOS } from '@/hooks/use-despia';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';

interface PurchaseEntry {
  entitlementId: string;
  isActive: boolean;
  productId?: string;
  [key: string]: any;
}

/**
 * Check local Apple receipt for active "BuxBox Pro" entitlement via Despia.
 * Returns the active purchase entry or null.
 */
export async function checkDespiaEntitlement(): Promise<PurchaseEntry | null> {
  if (!isDespiaIOS()) return null;

  try {
    const despia = (await import('despia-native')).default;
    const data = await despia('getpurchasehistory://', ['restoredData']);
    const purchases: PurchaseEntry[] = Array.isArray(data?.restoredData)
      ? data.restoredData
      : [];

    const activePurchase = purchases.find(
      (p) =>
        p.isActive &&
        (p.entitlementId === 'BuxBox Pro' || p.entitlementId === 'premium')
    );

    console.log(
      '[EntitlementCheck] Active BuxBox Pro:',
      !!activePurchase,
      '(total purchases:',
      purchases.length,
      ')'
    );
    return activePurchase ?? null;
  } catch (err) {
    console.warn('[EntitlementCheck] Failed to check purchase history:', err);
    return null;
  }
}

/**
 * Full post-auth sync: check device entitlement → sync to Supabase via edge function.
 * Passes source: "device_receipt" so the function upserts directly from trusted device data.
 */
export async function syncEntitlementToUser(userId: string): Promise<boolean> {
  const activePurchase = await checkDespiaEntitlement();
  if (!activePurchase) return false;

  try {
    const { data, error } = await supabase.functions.invoke(
      'sync-revenuecat-subscription',
      {
        body: {
          source: 'device_receipt',
          product_id: activePurchase.productId || '',
          entitlement_id: activePurchase.entitlementId || 'BuxBox Pro',
        },
      }
    );

    if (!error && data?.synced) {
      console.log('[EntitlementCheck] Subscription synced to Supabase for:', userId);
      return true;
    }
    console.warn('[EntitlementCheck] Sync response:', data || error);
  } catch (err) {
    console.error('[EntitlementCheck] Edge function sync failed:', err);
  }

  return false;
}

/**
 * React hook for checking entitlements on app launch.
 * Returns { isChecking, hasSubscription } to gate /iosflow redirect.
 */
export function useDespiaEntitlementCheck() {
  const [isChecking, setIsChecking] = useState(false);
  const [hasSubscription, setHasSubscription] = useState<boolean | null>(null);
  const checkedRef = useRef(false);
  const queryClient = useQueryClient();

  const check = useCallback(
    async (userId?: string) => {
      if (checkedRef.current) return hasSubscription ?? false;
      if (!isDespiaIOS()) {
        setHasSubscription(false);
        return false;
      }

      checkedRef.current = true;
      setIsChecking(true);

      try {
        const activePurchase = await checkDespiaEntitlement();
        const hasEntitlement = !!activePurchase;
        setHasSubscription(hasEntitlement);

        if (hasEntitlement && userId) {
          const synced = await syncEntitlementToUser(userId);
          if (synced) {
            queryClient.invalidateQueries({ queryKey: ['subscriptionStatus'] });
            localStorage.setItem('onboarding_completed', 'true');
          }
        }

        return hasEntitlement;
      } catch (err) {
        console.error('[EntitlementCheck] Check failed:', err);
        setHasSubscription(false);
        return false;
      } finally {
        setIsChecking(false);
      }
    },
    [hasSubscription, queryClient]
  );

  return { isChecking, hasSubscription, check };
}
